// Pure leave-hours calculation used by FormlyFieldDateHoursCalculator.
// Kept separate from the component so the logic is unit-testable without a render tree.
//
// Workday window used to derive leave hours from the start/end datetimes.
// Half-day model: a day is split at midday; the morning and afternoon each count as 0.5 day,
// a full day as HOURS_PER_DAY hours. The first/last day only count the halves the entered
// times actually cover.
// TODO: this should ultimately be calculated in the backend (with a configurable window).
const DAY_START_MIN = 8 * 60; // 08:00
const DAY_END_MIN = 17 * 60; // 17:00
const SPLIT_MIN = 12 * 60; // 12:00 (midday split between morning/afternoon)
const HOURS_PER_DAY = 8;

// A date-only value (YYYY-MM-DD, no time component) means the user picked a day but
// did not narrow it to a half-day — so it should count as a whole day on both ends.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses a leave boundary into a local Date. When only a date was entered (no time),
 * a start anchors to the very start of the day (00:00) and an end to the very end
 * (23:59:59), so a plain date range counts as whole days while an explicit time still
 * produces a half-day.
 */
function toBoundaryDate(value: string | Date, edge: 'start' | 'end'): Date {
  if (typeof value === 'string' && DATE_ONLY.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return edge === 'start' ? new Date(y, m - 1, d, 0, 0, 0) : new Date(y, m - 1, d, 23, 59, 59);
  }
  return value instanceof Date ? value : new Date(value);
}

/**
 * Number of leave hours between two datetimes, using a fixed workday window (08:00–17:00)
 * and a midday split (12:00). Each calendar day contributes a morning and/or afternoon
 * half-day (0.5 day each); the first/last day only count the halves the entered times cover.
 *
 * A date-only boundary (no time entered) counts as a whole day: the start anchors to 00:00,
 * the end to 23:59:59.
 *
 * Example: Mon 13:00 → Wed 12:00 = 0.5 (Mon afternoon) + 1 (Tue) + 0.5 (Wed morning) = 2 days = 16h.
 *
 * Returns 0 for invalid input (unparseable dates or end before start).
 */
export function calculateLeaveHours(startValue: string | Date, endValue: string | Date): number {
  const start = toBoundaryDate(startValue, 'start');
  const end = toBoundaryDate(endValue, 'end');

  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end.getTime() < start.getTime()) {
    return 0;
  }

  const startMin = start.getHours() * 60 + start.getMinutes();
  const endMin = end.getHours() * 60 + end.getMinutes();

  // Date-only cursors so we iterate whole calendar days regardless of time component.
  const firstDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const lastDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const cursor = new Date(firstDay);

  let days = 0;
  while (cursor.getTime() <= lastDay.getTime()) {
    const isFirst = cursor.getTime() === firstDay.getTime();
    const isLast = cursor.getTime() === lastDay.getTime();

    const fromMin = isFirst ? startMin : DAY_START_MIN;
    const toMin = isLast ? endMin : DAY_END_MIN;

    const coversMorning = fromMin < SPLIT_MIN;
    const coversAfternoon = toMin > SPLIT_MIN;

    days += (coversMorning ? 0.5 : 0) + (coversAfternoon ? 0.5 : 0);
    cursor.setDate(cursor.getDate() + 1);
  }

  return days * HOURS_PER_DAY;
}
